import React from 'react';
import { CapabilityNoetivraNote } from './CapabilityNoetivraNote';
import { motion } from 'framer-motion';
import { Users, Target, CheckCircle, XCircle } from 'lucide-react';
export function SocialCooperationPage() {
  return <div className="min-h-screen bg-surface-bg" data-id="element-1522" data-mp="3e72f9lpszt10">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-1523" data-mp="3j76w5cumiprr">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-1524" data-mp="3o6tajpm6uc25">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-1525" data-mp="5rr18l8q9s0b">
            <span className="w-8 h-px bg-teal" data-id="element-1526" data-mp="5u4v4qro8y6a"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-1527" data-mp="arwolp7ysscl">
              Capability
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-1528" data-mp="fqga5oinc24f"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-1529" data-mp="q41ganod35kv">
            Social Cooperation
          </h1>

          <p className="text-xl text-ink-muted leading-relaxed max-w-3xl mx-auto" data-id="element-1530" data-mp="g4u6ubkqzv31">
            At SyNeural, social cooperation refers to the ability to work
            constructively with other people, navigate disagreement, interpret
            social dynamics accurately, and contribute to shared goals without
            unnecessary conflict, chaos, or withdrawal.
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1531" data-mp="z2dfhe3anbox">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1532" data-mp="v2wa0knvaejr">
          <p className="text-lg text-ink-muted leading-relaxed mb-6" data-id="element-1533" data-mp="3h25v52zfb31s">
            It includes not only interpersonal skill, but the broader capacity
            to function within groups, teams, communities, and institutions in
            ways that build trust rather than erode it.
          </p>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1534" data-mp="3c9cqsg7v9wq8">
            This capability matters because no serious society, institution, or
            organization can function well if too many people are unable to
            cooperate. Even highly intelligent individuals can weaken systems if
            they are chronically reactive, combative, distrustful,
            status-obsessed, or unable to coordinate with others. Social
            cooperation is one of the core capacities that allows human ability
            to scale into functioning institutions and communities.
          </p>
        </motion.div>
      </section>

      {/* Why It Matters */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface-muted border-y border-line" data-id="element-1535" data-mp="2c1y8qylkzzhp">
        <div className="max-w-4xl mx-auto" data-id="element-1536" data-mp="2734vn7a7iai2">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-1537" data-mp="274fvwx4g104f">
            <div className="flex items-center gap-4 mb-6" data-id="element-1538" data-mp="2uuk0p7tdb5x0">
              <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-1539" data-mp="2qqc8v1rm59uk">
                <Target size={24} data-id="element-1540" data-mp="34qdaijfpclb3" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1541" data-mp="2zraetplm3rfs">
                Why It Matters
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1542" data-mp="1addxmy2pi57i">
              Social cooperation influences whether people can handle conflict
              productively, participate in teams, build trust, and remain
              effective in shared environments. Weak cooperation leads to
              fragmentation, conflict escalation, alienation, and institutional
              dysfunction. Strong cooperation supports healthier communities,
              better teams, more stable organizations, and stronger civic
              participation.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What Strong Social Cooperation Looks Like */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1543" data-mp="t4l4lwyny8mj">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1544" data-mp="y2wci25ee924">
          <div className="flex items-center gap-4 mb-6" data-id="element-1545" data-mp="ac8u2mxuw11b">
            <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-1546" data-mp="aapcmt0grjxy">
              <CheckCircle size={24} data-id="element-1547" data-mp="2nkz6gv9gplcr" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1548" data-mp="2jn1itwvcb7bh">
              What Strong Social Cooperation Looks Like
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1549" data-mp="29nsp954dzupn">
            People with strong social cooperation can disagree without
            immediately collapsing into hostility or withdrawal. They are able
            to listen, negotiate, coordinate, and contribute within group
            settings. They can recognize the difference between necessary
            disagreement and destructive conflict. They also tend to understand
            that shared systems require mutual responsibility, not just
            individual preference.
          </p>
        </motion.div>
      </section>

      {/* What Weak Social Cooperation Looks Like */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white border-y border-line" data-id="element-1550" data-mp="1gzn5olp2kbre">
        <div className="max-w-4xl mx-auto" data-id="element-1551" data-mp="17ahihe7yg1ab">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-1552" data-mp="1cace1v2odf1p">
            <div className="flex items-center gap-4 mb-6" data-id="element-1553" data-mp="1q2saue4zggu6">
              <div className="w-12 h-12 rounded-lg bg-navy-light flex items-center justify-center text-white" data-id="element-1554" data-mp="1v0tyzdh9n6ci">
                <XCircle size={24} data-id="element-1555" data-mp="1h9yoxxv16j7c" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1556" data-mp="1h96votowoxk8">
                What Weak Social Cooperation Looks Like
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1557" data-mp="28zjwuvbwta1x">
              Weak social cooperation may appear as chronic conflict, inability
              to handle dissent, social volatility, distrust, rigid ego-defense,
              poor group awareness, or withdrawal from shared responsibility. In
              practical terms, this can damage teams, weaken institutions,
              reduce local trust, and make civic life more unstable and
              fragmented.
            </p>
          </motion.div>
        </div>
      </section>

      {/* How It Can Be Strengthened */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1558" data-mp="3fjxj1qf1grof">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1559" data-mp="3ak1ffsa9729e">
          <div className="flex items-center gap-4 mb-6" data-id="element-1560" data-mp="14qbfp9v2ugvc">
            <div className="w-12 h-12 rounded-lg bg-green-soft flex items-center justify-center text-white" data-id="element-1561" data-mp="14sxofctpf2f5">
              <Users size={24} data-id="element-1562" data-mp="zu2tjcigpneo" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1563" data-mp="2tbifinmjxsyc">
              How It Can Be Strengthened
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1564" data-mp="2y77dimixb1sj">
            Social cooperation can be developed through conflict-resolution
            training, communication practice, teamwork structures, mentorship,
            civic engagement, emotional regulation work, and environments that
            reward constructive contribution instead of performative hostility.
            In the SyNeural framework, social cooperation is essential because
            strong judgment alone is not enough if a person cannot function well
            with others inside shared systems.
          </p>
        </motion.div>
      </section>
      <CapabilityNoetivraNote capability="Social cooperation" data-id="element-1565" data-mp="1rlws6faxd64u" />
    </div>;
}